// =============================================================================
// FILE: packages/renderer/src/stores/pins.store.ts
//
// PURPOSE: Microsoft-style pin persistence for any "pinnable" UI surface.
//          Currently consumed by the Cyberpunk Launcher and the Sidebar.
//          Storage is keyed by namespace so launcher pins and sidebar
//          pins are independent.
//
// PERSISTENCE
//   localStorage key: `pins.<namespace>` → JSON string[] (stable order
//   the user pinned them).  We store ids (not labels) so labels can
//   change without breaking saved state.  No external IPC — pins are
//   per-workstation preference; if WorkspacePrefs ever centralises this
//   we can swap the persistence layer in one place.
//
// ARCHITECTURAL RULE
//   Every UI module that appears in a "pinnable surface" (Launcher,
//   Sidebar, future docks) MUST consume `usePins(namespace)` rather
//   than rolling its own toggle / storage. This is enforced by code
//   review — no other module may write to `localStorage` keys
//   matching /^pins\..+/.
// =============================================================================

import { useSyncExternalStore } from 'react';

type Namespace = 'launcher' | 'sidebar';

const subs = new Map<Namespace, Set<() => void>>();
const KEY = (ns: Namespace): string => `pins.${ns}`;

// Cached snapshots — useSyncExternalStore requires `getSnapshot` to return
// the SAME reference until the underlying state actually changes.
const snapshots = new Map<Namespace, string[]>();
function readCached(ns: Namespace): string[] {
  let cur = snapshots.get(ns);
  if (cur === undefined) { cur = load(ns); snapshots.set(ns, cur); }
  return cur;
}
function refreshSnapshot(ns: Namespace, next: string[]): void {
  snapshots.set(ns, next);
  const set = subs.get(ns);
  if (set) for (const fn of set) fn();
}

function load(ns: Namespace): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(KEY(ns));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch { return []; }
}

function save(ns: Namespace, ids: string[]): void {
  localStorage.setItem(KEY(ns), JSON.stringify(ids));
  refreshSnapshot(ns, ids);
}

export const pinsStore = {
  get(ns: Namespace): string[] { return readCached(ns); },
  isPinned(ns: Namespace, id: string): boolean { return readCached(ns).includes(id); },
  toggle(ns: Namespace, id: string): void {
    const cur = readCached(ns);
    save(ns, cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  },
  pin(ns: Namespace, id: string): void {
    const cur = readCached(ns);
    if (!cur.includes(id)) save(ns, [...cur, id]);
  },
  unpin(ns: Namespace, id: string): void {
    save(ns, readCached(ns).filter((x) => x !== id));
  },
  subscribe(ns: Namespace, fn: () => void): () => void {
    const set = subs.get(ns) ?? new Set();
    set.add(fn); subs.set(ns, set);
    return () => set.delete(fn);
  },
};

/** React hook — returns `{ pinned, isPinned, toggle }`. */
export function usePins(ns: Namespace): {
  pinned:    string[];
  isPinned:  (id: string) => boolean;
  toggle:    (id: string) => void;
} {
  const pinned = useSyncExternalStore(
    (cb) => pinsStore.subscribe(ns, cb),
    () => readCached(ns),
    () => [],
  );
  return {
    pinned,
    isPinned: (id) => pinned.includes(id),
    toggle:   (id) => pinsStore.toggle(ns, id),
  };
}
